import { useState, useEffect, useRef, useCallback } from "react";
import dynamic from "next/dynamic";
import Error from "../_error";

import { dehydrate, QueryClient, useQuery } from '@tanstack/react-query';
import { hasProperty, isEmpty } from "../../utils/validate";
import { getQueryFn, queryKeyManager } from "../../utils/api/queryFunction";
import queryConfig from "../../utils/api/queryConfig";
import { addDataLayerEvent } from "../../utils/data.layer.utils/data.layer.util";

const SEO = dynamic(() => import("../../components/Shared/SEO"));
const CampaignLayout = dynamic(() => import("../../components/Layout/CampaignLayout"));
const HeroBanner = dynamic(() => import("../../components/Organisms/Headers/HeroBanner"));
const CampaignBuilder = dynamic(() => import("../../components/Shared/Builders/CampaignBuilder"));
const BATDPopUp = dynamic(() => import("../../components/Category/BATD/BATDPopUp"));
const SubHeader = dynamic(() => import("../../components/Header/SubHeader"));

const endpoint = "cm";

const getSubNav = (builder) => {
	let subNav = [];

	// logic for constructing nav based on campaign blocks
	if (builder) {
		builder.forEach((item, index) => {
			if (item.data && "hasNavListing" in item.data && item.data.hasNavListing) {
				subNav.push({
					id: index,
					title: item.data.navTitle || item.data.title || '',
				});
			}
		});
	}

	return subNav;
};


const CampaignPage = props => {
	const { slug, token } = props;

	const { isLoading, isError, data } = useQuery({
		queryKey: queryKeyManager(["campaign"], [slug, token]),
		queryfn: () => getQueryFn(`${endpoint}/${slug}`, token),
		...queryConfig
	});

	if (isLoading) {
		return <span />
	}

	if (hasProperty(data, 'error')) {
		return <Error error={data.error} errorCode={data.error.code} />
	}

	const [openBATD, setOpenBATD] = useState(false);
	const [formData, setFormData] = useState();

	const [productSelected, setSelectedProduct] = useState();
	const [formProduct, setFormProduct] = useState();
	const [pageViewed, setPageViewed] = useState(false);
	const [nav, setNav] = useState(null);

	const banner = useRef(null);

	const getFormData = useCallback(async (formHandle) => {
		const res = await getQueryFn(`form/${formHandle}`);
		setFormData(res);
		return res;
	}, [getQueryFn, setFormData]);

	useEffect(async () => {
		if (hasProperty(data, 'form') && !isEmpty(data.form)) {
			if (hasProperty(data.form, 'handle')) {
				await getFormData(data.form.handle);
			}

			if (hasProperty(data, 'product') && !isEmpty(data.product)) {
				setFormProduct(data.product);
			}
		}
	}, [data]);

	useEffect(
		() => productSelected && setFormProduct(productSelected),
		[productSelected]
	);

	useEffect(() => {
		if (!pageViewed) {
			addDataLayerEvent('CampaignPageView', {
				title: data.title,
				url: data.url,
				type: data.type,
				category: "category" in data && data.category ? data.category : null,
				slug: "slug" in data && data.slug ? data.slug : null,
			});
			setPageViewed(true);
		}
	}, [pageViewed]);

	useEffect(() => {
		if (data && data.builder) {
			setNav(getSubNav(data.builder));
		}
	}, [data]);

	const onCampaignBuilderProductChange = useCallback(
		() => !productSelected && setSelectedProduct(productSelected),
		[productSelected]
	);

	return (
		<div style={{ backgroundColor: data.page.pageColour ? data.page.pageColour : '#FFF' }}>
			<SEO seo={data.seo} />
			{/* TODO: Manipulate the Page based on parameteres that will be passed */}
			<CampaignLayout
				type={data.type}
				nav={data.page.nav.header.links}
				logo={data.page.nav.header.logos[data.page.logo]}
				footer={data.page.nav.footer}
			>
				<HeroBanner
					category={'campaign'}
					slug={data.slug}
					banner={data.banner}
					setOpenBATD={setOpenBATD}
					getFormData={getFormData}
					refElement={banner}
				/>
				{/* Add content builder */}
				{!!nav && nav.length > 0 &&
					<SubHeader
						nav={nav}
						refElement={banner}
					/>}
				{/* CAMPAIGN BUILDER */}
				<div className={`campaignBuilderHolder fc-${data.page.fontColour}`} >
					<div className={`campaignBuilder ${data.banner.media === null ? 'pt-85' : ''}`} >
						<CampaignBuilder
							data={data}
							setSelectedProduct={onCampaignBuilderProductChange}
							setOpenBATD={setOpenBATD}
							getFormData={getFormData}
						/>
					</div>
				</div>
				{formData && formProduct && formProduct.models && formProduct.models.length > 0 &&
					<BATDPopUp
						isActive={openBATD}
						product={formProduct}
						models={formProduct.models}
						close={setOpenBATD}
						category={data.category}
						formData={formData}
						featuredImage={formProduct.models[0].featureImage}
						title={formData ? formData.name : ""}
						spec={data.dealerSpec}
						thankYou="/cm/thank-you"
					/>}
			</CampaignLayout>

		</div>
	);
};

CampaignPage.getInitialProps = async ({ query }) => {
	const queryClient = new QueryClient();
	const { slug, token } = query;

	await queryClient.prefetchQuery({
		queryKey: queryKeyManager(["campaign"], [slug, token]),
		queryFn: () => getQueryFn(`${endpoint}/${slug}`, token),
		...queryConfig
	});

	return {
		slug: slug,
		token: token,
		dehydratedState: dehydrate(queryClient)
	};
};

export default CampaignPage;